import moment from "moment";
import { defineStore } from "pinia";
import { Container } from "aurelia-dependency-injection";
import config from "../../common/config";
import { GroupedAgencies } from "../../resources-vue/vue-models/agency";
import {
    ApplicationEnum,
    ConversationRoomTypeEnum,
    ConversationTypeEnum,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../enums/enums";
import { getSolutionAgencyIds } from "../../common/vue-helpers/solution-agencies";
import { PermissionManager } from "../../common/utilities/permission-manager";
import { AgencyService, IGetOrganization } from "../../services/agency.service";
import { ISetUserProfile, IUserStatusDetails } from "../../resources-vue/vue-interfaces/messaging-service/i-user";
import { AxxessIntelligenceService } from "../../services/axxess-intelligence.service";
import { MessageService } from "../../services/message-service";
import { ProfileActivityStatusEnum } from "../../enums/ChatUserProfileEnum";
import { useConversationStore } from "../MessageCenter/store/useConversationStore";
import { Role } from "../../models/role.model";
import { IRoles } from "../../resources-vue/vue-interfaces/i-roles";
import { RolesService } from "../../services/roles.service";
import AuthService from "../../services/auth.service";

const LOAD_ROLES = "loadRoles";
const SET_ROLES = "SET_ROLES";
const GET_ROLES_STRING = "getRolesInString";
const GET_EXISTING_COOKIE = "getExistingCookie";
const SET_COOKIE = "setCookie";
const SET_USER_SUB_ID = "setUserSubId";
const SET_LOGGEDIN_USER_DETAILS = "setLoggedInUserDetails";
const SHOW_LOGO = "SHOW_LOGO";
const SET_AGENCIES = "SET_AGENCIES";
const GET_SOLUTION_AGENCY_IDS = "getSolutionAgencyIds";
const SET_ORGANIZATIONS = "SET_ORGANIZATIONS";
const LOAD_CONNECT_PERMISSION = "loadConnectPermission";
const SET_FREQUENCY_WATCH = "setFrequencyWatch";
const SET_HEADER = "setHeader";

export const useApp = defineStore("useApp", {
    state: () => ({
        roles: [] as any,
        rolesPromise: null as Promise<void> | null,
        hasPlannerPermission: false as boolean,
        plannerPermissionLoaded: false as boolean,
        cookieValue: "" as string,
        userSubId: "" as string,
        showLogo: false,
        loggedInUserDetails: {} as IUserStatusDetails,
        agencies: null as any,
        organizations: null as IGetOrganization[],
        connectPermissionLoaded: false as boolean,
        connectPermissionPromise: null as Promise<void> | null,
        hasAxxessConnectPermission: false as boolean,
        isAnyPremiumAgency: false as boolean,
        showFrequencyWatch: true as boolean,
        isClinician: false as boolean,
        showHHNav: true as boolean,
        showHosNav: true as boolean,
        showMainHeader: true
    }),
    actions: {
        async [LOAD_ROLES](): Promise<void> {
            if (this.plannerPermissionLoaded) {
                return;
            }
            if (this.rolesPromise) {
                await this.rolesPromise;
                return;
            }

            this.rolesPromise = (async () => {
                try {
                    await Container.instance.get(AuthService).ensureAccessToken();
                    const _rolesService = Container.instance.get(RolesService);
                    let roles: Array<IRoles> = await _rolesService.getRoles();
                    this.roles = roles.map((role) => new Role(role));

                    let isClinician = this.roles.some(
                        (role: Role) => role.isClinician === true || role.isAdmin === true
                    );
                    // HOS and PAL don't have roles, they have titles and can view permission for clinician to decide. So we need to check if the user has permission for clinician in HOS and PAL agency. And in future we may also add title check for HOS and PAL just like role.
                    if (!isClinician) {
                        try {
                            const permissionManager = Container.instance.get(PermissionManager);
                            if (!permissionManager.isPermissionManagerReady) {
                                await permissionManager.refreshUserPermissionData();
                            }
                            isClinician =
                                permissionManager.checkPermissionByProduct(
                                    ParentPermissionEnum.clinician,
                                    PermissionActionEnum.canView,
                                    ApplicationEnum.AxxessHospiceFE
                                ) ||
                                permissionManager.checkPermissionByProduct(
                                    ParentPermissionEnum.clinician,
                                    PermissionActionEnum.canView,
                                    ApplicationEnum.AxxessPalliative
                                );
                        } catch (error) {
                            console.error(error);
                        }
                    }

                    this.isClinician = isClinician;
                } finally {
                    this.plannerPermissionLoaded = true;
                }
            })();

            await this.rolesPromise;
        },
        [SET_ROLES](payload: any) {
            this.roles = payload;
        },
        [GET_ROLES_STRING]() {
            const location = JSON.parse(localStorage.getItem("axxess-user-location"));
            let applicationRoles = {} as any;
            this.roles.forEach((role: any) => {
                applicationRoles[role.Application] = [...role.Roles];
            });
            const applicationKeys = Object.keys(applicationRoles);
            let cookieValue = "";
            applicationKeys.map((application: any, appIndex: number) => {
                const roles = applicationRoles[application];
                cookieValue = cookieValue.concat(
                    `${application}:${roles.map((role: any, index: number) => {
                        if (index === roles.length - 1) {
                            return `${role}|`;
                        } else {
                            return `${role}`;
                        }
                    })}`
                );
            });
            cookieValue = cookieValue.concat(`${location?.city},${location?.state}`);
            this.cookieValue = cookieValue;
        },
        [GET_EXISTING_COOKIE](cookieName: string) {
            let name = cookieName + "=";
            let decodedCookie = decodeURIComponent(document.cookie);
            let ca = decodedCookie.split(";");
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) == " ") {
                    c = c.substring(1);
                }
                if (c.indexOf(name) == 0) {
                    return c.substring(name.length, c.length);
                }
            }
            return "";
        },
        [SET_COOKIE]() {
            if (this.getExistingCookie("axxess.identity.meta") !== "") {
                document.cookie = `axxess.identity.meta=;path=/;expires=${moment().subtract(1, "days")}`;
            }
            this.getRolesInString();
            const domain = config.isStage ? ".axxess.tech" : ".axxessweb.com";
            document.cookie = `axxess.identity.meta=${this.cookieValue};path=/;domain=${domain};SameSite=None;Secure`;
        },
        [SET_USER_SUB_ID](userId: string) {
            this.userSubId = userId;
        },
        [SET_LOGGEDIN_USER_DETAILS](details: IUserStatusDetails) {
            this.loggedInUserDetails = details;
        },
        [SHOW_LOGO](payload: boolean) {
            this.showLogo = payload;
        },
        [SET_AGENCIES](payload: Map<string, GroupedAgencies>) {
            this.agencies = payload;
        },
        [GET_SOLUTION_AGENCY_IDS](applicationIds: Set<ApplicationEnum>) {
            return getSolutionAgencyIds(this.agencies, applicationIds);
        },
        [SET_ORGANIZATIONS](payload: IGetOrganization[]) {
            this.organizations = payload;
        },
        async [LOAD_CONNECT_PERMISSION]() {
            // already completed loading
            if (this.connectPermissionLoaded) {
                return this.isAnyPremiumAgency && this.hasAxxessConnectPermission;
            }

            // currently loading
            if (this.connectPermissionPromise) {
                await this.connectPermissionPromise;
                return this.isAnyPremiumAgency && this.hasAxxessConnectPermission;
            }

            const agencyService = Container.instance.get(AgencyService);
            const axxessIntelligenceService = Container.instance.get(AxxessIntelligenceService);
            const messageService = Container.instance.get(MessageService);

            // neccesary call chain sequence
            this.connectPermissionPromise = (async () => {
                try {
                    await Container.instance.get(AuthService).ensureAccessToken();
                    this.organizations = await agencyService.getOrganizations();

                    const res = await messageService.getAxxessConnectToggle();
                    this.hasAxxessConnectPermission = res ? res.agencyPermission : false;
                    this.isAnyPremiumAgency = res ? res.isPremium : false;

                    if (this.isAnyPremiumAgency && this.hasAxxessConnectPermission) {
                        // resets the user activity once user login.
                        const convStore = useConversationStore();
                        const value: boolean = JSON.parse(localStorage.getItem("showConnectTutorial")); // null if not found
                        convStore.setShowChatTutorial(value);
                        const payload: ISetUserProfile = {
                            login: this.loggedInUserDetails.loginId,
                            userActivityStatus: ProfileActivityStatusEnum.ResetActivity
                        };
                        await messageService.updateUserProfile(payload);
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this.connectPermissionLoaded = true;
                }
            })();

            // waits for complete chain
            try {
                await this.connectPermissionPromise;
            } finally {
                this.connectPermissionPromise = null;
            }

            return this.hasAxxessConnectPermission && this.isAnyPremiumAgency;
        },
        [SET_FREQUENCY_WATCH](payload: any) {
            ((this.showFrequencyWatch = payload.showFrequencyWatch),
                (this.showHHNav = payload.showHHNav),
                (this.showHosNav = payload.showHosNav));
        },
        [SET_HEADER](payload) {
            this.showMainHeader = payload;
        }
    }
});
